/**
 * @fileoverview A recording saved in this browser: why it is waiting, and the
 * one fix that applies (iOS review B6).
 *
 * A take is written to the upload queue before its upload starts, so a save
 * that cannot finish is never lost. What used to be eight different toasts
 * is one outcome, "Saved in this browser", with a reason line that varies.
 * The recorder's saved stage and the Saved recordings page both read the
 * reason through these helpers, so they say the same thing and offer the
 * same way forward. A mirror of `mobile/src/utils/savedRecordings.js`.
 */
import { canRequestConsent } from "@/lib/consent";
import { isProgramRosterStudent } from "@/lib/importedStudents";

/** The server's structured refusal codes a save can land on. */
const GATE_CODES = ["minutes_exhausted", "ai_not_included", "pro_required", "feature_disabled"];

/**
 * Why an upload did not finish, from the error it threw.
 *
 * @param {Error} error - What the upload rejected with.
 * @returns {{code: string, message: string|null, context?: string, contextName?: string, pendingStudents?: string|null}}
 *   `code` is one of: network, processing, ai_disclosure_required,
 *   consent_required, minutes_exhausted, ai_not_included, pro_required,
 *   feature_disabled, rejected.
 */
export function blockedReason(error) {
  if (error?.confirmFailed && error.lessonId) {
    return { code: "processing", message: null };
  }
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    if (detail.code === "ai_disclosure_required") return { code: "ai_disclosure_required", message: detail.message || null };
    if (detail.code === "consent_required") {
      const names = (detail.pending_students || []).filter(Boolean).join(", ");
      return { code: "consent_required", message: detail.message || null, pendingStudents: names || null };
    }
    if (GATE_CODES.includes(detail.code)) {
      return {
        code: detail.code,
        message: detail.message || null,
        context: detail.context || "personal",
        contextName: detail.context_name || null,
      };
    }
  }
  if (status === 403 || status === 400 || status === 422) {
    const message = typeof detail === "string" ? detail : detail?.message || null;
    return { code: "rejected", message };
  }
  return { code: "network", message: null };
}

/** The reason an organization's exhausted pool is nobody's to fix here. */
const orgPool = (blocked) => blocked?.code === "minutes_exhausted" && blocked.context && blocked.context !== "personal";

/**
 * What a saved take is waiting for, in a few words, and the action that can
 * move it: the Saved recordings page's row, and the recorder's saved stage.
 *
 * @param {{code: string, message?: string|null}|null|undefined} blocked
 * @param {Object} [options]
 * @param {Object|null} [options.student] - The take's student, for consent actions.
 * @returns {{line: string, action: 'retry'|'consent'|'plan'|'notice'|null, label: string|null}}
 */
export function savedFix(blocked, { student = null } = {}) {
  const code = blocked?.code || "network";
  switch (code) {
    case "processing":
      return { line: "Processing didn’t start", action: "retry", label: "Try again" };
    case "ai_disclosure_required":
      return { line: "AI notice not agreed yet", action: "notice", label: "Review notice" };
    case "consent_required": {
      if (student && isProgramRosterStudent(student)) {
        return { line: "Consent comes from the organization", action: null, label: null };
      }
      if (student && canRequestConsent(student)) {
        const requested = student.consent_status === "requested";
        return {
          line: requested ? "Consent requested · waiting on the family" : "Consent not on file",
          action: "consent",
          label: requested ? "Resend request" : "Request consent",
        };
      }
      const who = blocked?.pendingStudents ? ` for ${blocked.pendingStudents}` : "";
      return { line: `Consent not on file${who}`, action: "retry", label: "Try again" };
    }
    case "minutes_exhausted":
      if (orgPool(blocked)) {
        return { line: `${blocked.contextName || "Your organization"} is out of minutes · ask your admin`, action: null, label: null };
      }
      return { line: "Out of minutes", action: "plan", label: "Plan & usage" };
    case "ai_not_included":
    case "pro_required":
      return { line: "Needs a plan with lesson AI", action: "plan", label: "Plan & usage" };
    case "feature_disabled":
      return { line: "Turned off by your organization", action: null, label: null };
    case "rejected":
      return { line: blocked?.message || "The server refused it", action: "retry", label: "Try again" };
    case "network":
    default:
      return { line: "Waiting for a connection", action: "retry", label: "Try again" };
  }
}

/**
 * The sentence under "Saved in this browser" in the recorder.
 *
 * @param {{code: string, message?: string|null}|null|undefined} blocked
 * @param {Object} [options]
 * @param {string} [options.first] - The student's first name, when there is one student.
 * @param {Object|null} [options.student]
 * @returns {string}
 */
export function savedReasonLine(blocked, { first = null, student = null } = {}) {
  const kept = "Hans kept the recording";
  const code = blocked?.code || "network";
  switch (code) {
    case "processing":
      return "The upload landed but processing didn’t start. Try again sends it on.";
    case "ai_disclosure_required":
      return `The AI notice isn’t agreed yet. ${kept}; it sends once you agree.`;
    case "consent_required": {
      if (student && isProgramRosterStudent(student)) {
        return `Consent for ${first || "this student"} comes from the organization. ${kept}; it sends once consent is on file.`;
      }
      if (student?.consent_status === "requested") {
        return `Consent for ${first || "this student"} is requested and not answered yet. ${kept}; it sends once it’s accepted.`;
      }
      const who = first ? `Consent for ${first}` : blocked?.pendingStudents ? `Consent for ${blocked.pendingStudents}` : "Consent";
      return `${who} isn’t on file yet. ${kept}; it sends once consent is granted.`;
    }
    case "minutes_exhausted":
      if (orgPool(blocked)) {
        return `${blocked.contextName || "Your organization"} has no recording minutes left. ${kept}; ask your organization admin to add more.`;
      }
      return `You’re out of recording minutes. ${kept}; it sends once minutes are added.`;
    case "ai_not_included":
    case "pro_required":
      return `${blocked?.message || "This plan doesn’t include lesson AI."} ${kept}.`;
    case "feature_disabled":
      return `${blocked?.message || "Recording is turned off by your organization."} ${kept}.`;
    case "rejected":
      return `${blocked?.message || "The server refused the upload."} ${kept}, so you can try again.`;
    case "network":
    default:
      return `No connection right now. ${kept} and will send it when you try again.`;
  }
}

/**
 * "32:14", or "1:04:09" past an hour.
 * @param {number} seconds
 * @returns {string}
 */
export function clipLength(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

/**
 * "2 saved recordings waiting": Home's one-line card and the Lessons row.
 * @param {number} count
 * @returns {string}
 */
export function savedCountLabel(count) {
  return `${count} saved recording${count === 1 ? "" : "s"} waiting`;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * When a take was saved, the way a person says it: today, yesterday, a
 * weekday inside the week, else "Oct 12".
 * @param {string} createdAt - ISO timestamp.
 * @param {Date} [now]
 * @returns {string}
 */
export function savedWhen(createdAt, now = new Date()) {
  const at = new Date(createdAt);
  if (Number.isNaN(at.getTime())) return "";
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(at)) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return WEEKDAYS[at.getDay()];
  return `${MONTHS[at.getMonth()]} ${at.getDate()}`;
}
